<script setup lang="ts">
    import { computed, watch, onMounted, type Ref } from 'vue'

    import { SelectorSettingsReadModel } from "@Quest/PortfolioScenarios/readmodels"
    import { SelectorDialogViewModel } from "@Quest/PortfolioScenarios/Components/SelectorDialog"

    import { useSelectorSettingsQueryV1 } from "@Quest/PortfolioScenarios/queries-vue-composables"
    import { useOptimizeSelectionCommandV1 } from "@Quest/PortfolioScenarios/commands-vue-composables"

    // ======================
    // Props
    // ======================

    const props = defineProps<{
        viewModel: SelectorDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'selected', result: any): void
    }>()

    // ======================
    // Computed (replacing class getters)
    // ======================

    const vm = computed(() => props.viewModel)

    const opened = computed(() => vm.value?.opened ?? false)

    const workspaceId = computed(() => vm.value.workspaceId)
    const portfolioId = computed(() => vm.value.portfolioId)
    const scenarioId = computed(() => vm.value.scenarioId)

    // ======================
    // Query
    // ======================

    const {
        runSelectorSettingsQueryV1,
        modelSelectorSettingsQueryV1
    } = useSelectorSettingsQueryV1(workspaceId, portfolioId, scenarioId)

    const rm = modelSelectorSettingsQueryV1 as Ref<SelectorSettingsReadModel | null>

    // ======================
    // Command
    // ======================

    const {
        invokeOptimizeSelectionCommandV1,
        succeedOptimizeSelectionCommandV1,
        result: selectionResult
    } = useOptimizeSelectionCommandV1(workspaceId, portfolioId, scenarioId)

    // ======================
    // Watch opened (replaces watch + onOpenedChanged)
    // ======================

    watch(opened, (val) =>
    {
        if (val === true)
        {
            rm.value = null
            runSelectorSettingsQueryV1()
        }
        else
        {
            selectionResult.value = null
        }
    })



    // ======================
    // Command success watcher (replaces onCommandSucceed)
    // ======================

    watch(succeedOptimizeSelectionCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            close()
            emit("selected", selectionResult.value)
        }
    })

    // ======================
    // Methods
    // ======================

    function handleOptimizeSelectionCommand()
    {
        if (!rm.value) return

        const resources = rm.value.Resources.map(a => `${a.Id}:${a.Capacity}`)

        invokeOptimizeSelectionCommandV1({
            resources,
            totalCost: rm.value.TotalCost,
            opex: rm.value.Opex,
            capex: rm.value.Capex,
            othex: rm.value.Othex,
            expenses: rm.value.Expenses,
            opexExpenses: rm.value.OpexExpenses,
            applyBenefitsConfidenceFactor: rm.value.ApplyBenefitsConfidenceFactor
        })
    }

    function close()
    {
        vm.value.close()
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Selector</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div v-if="rm">
                            <div class="form-group">
                                <label>Risk adjusted benefits:</label>
                                <select class="form-control" v-model="rm.ApplyBenefitsConfidenceFactor">
                                    <option :value="true">Enabled</option>
                                    <option :value="false">Disabled</option>
                                </select>
                            </div>

                            <table class="table table-bordered">
                                <tbody>
                                    <tr>
                                        <th>Resource</th>
                                        <th style="width: 250px;" class="text-center">Limit</th>
                                    </tr>
                                    <tr v-for="r in rm.Resources" :key="r.Id">
                                        <td>
                                            {{r.Name}}<br />
                                            <small class="text-muted">{{r.Unit}}</small>
                                        </td>
                                        <td>
                                            <input type="number" class="form-control" v-model.number="r.Capacity" />
                                        </td>
                                    </tr>
                                </tbody>
                                <tbody>
                                    <tr>
                                        <th>Budget</th>
                                        <th style="width: 250px;" class="text-center">
                                            Limit <br />
                                            <small class="text-muted">{{rm.CurrencyLabel}}</small>
                                        </th>
                                    </tr>
                                    <tr>
                                        <td>
                                            Total cost
                                        </td>
                                        <td>
                                            <input type="number" class="form-control" v-model.number="rm.TotalCost" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Capex
                                        </td>
                                        <td>
                                            <input type="number" class="form-control" v-model.number="rm.Capex" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Opex
                                        </td>
                                        <td>
                                            <input type="number" class="form-control" v-model.number="rm.Opex" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Other expenses
                                        </td>
                                        <td>
                                            <input type="number" class="form-control" v-model.number="rm.Othex" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Expenses (All)
                                        </td>
                                        <td>
                                            <input type="number" class="form-control" v-model.number="rm.Expenses" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Expenses (Opex)
                                        </td>
                                        <td>
                                            <input type="number" class="form-control" v-model.number="rm.OpexExpenses" />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        <button @click="handleOptimizeSelectionCommand" type="button" class="btn btn-success">
                            <i class="fa-solid fa-robot fa-bounce mr-1"></i> Optimize now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>